import { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Lock,
  ChevronDown,
  Upload,
  RotateCcw,
  Eye,
} from 'lucide-react'
import {
  accountingApi,
  type JournalEntry,
  type Account,
  type TaxRule,
} from '@/modules/accounting/api/accounting.api'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore } from '@/stores/auth.store'
import { ROUTES } from '@/router/routes'

// ─── Helpers ─────────────────────────────────────────────────────────────────

import { toNum } from '@/lib/decimal'

const formatXOF = (n: any) => {
  const v = toNum(n)
  return v === 0
    ? '—'
    : new Intl.NumberFormat('fr-SN', { minimumFractionDigits: 0 }).format(Math.round(v)) + ' XOF'
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  DRAFT: { label: 'Brouillon', color: 'bg-[var(--bd2)]/10 text-[var(--tx3)]' },
  PENDING: { label: 'En attente', color: 'bg-[var(--amber-bg)] text-[var(--amber-text)]' },
  VALIDATED: { label: 'Validée', color: 'bg-[var(--green)]/10 text-[var(--green-text)]' },
  LOCKED: { label: 'Verrouillée', color: 'bg-[var(--blue-bg)] text-[var(--blue-d)]' },
  REVERSED: { label: 'Extournée', color: 'bg-[var(--red-bg)] text-[var(--red-text)]' },
}

interface LineForm {
  id: string
  debitAccountCode: string
  creditAccountCode: string
  debitAmount: string
  creditAmount: string
  description: string
  taxRuleCode: string
}

const emptyLine = (): LineForm => ({
  id: String(Date.now() + Math.random()),
  debitAccountCode: '',
  creditAccountCode: '',
  debitAmount: '',
  creditAmount: '',
  description: '',
  taxRuleCode: '',
})

// ─── Composant AccountSelect ──────────────────────────────────────────────────

function AccountSelect({
  value,
  onChange,
  accounts,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  accounts: Account[]
  placeholder: string
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')

  const filtered = accounts
    .filter((a) => a.isActive && !a.requiresApproval)
    .filter((a) => !q || a.code.includes(q) || a.name.toLowerCase().includes(q.toLowerCase()))
    .slice(0, 20)

  const selected = accounts.find((a) => a.code === value)

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-1 px-2 py-1.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded text-xs text-left hover:border-blue-500/50 transition-colors"
      >
        {selected ? (
          <span className="flex items-center gap-1.5 min-w-0">
            <code className="text-[var(--blue-d)] font-mono">{selected.code}</code>
            <span className="text-[var(--tx2)] truncate">{selected.name}</span>
          </span>
        ) : (
          <span className="text-[var(--tx3)]">{placeholder}</span>
        )}
        <ChevronDown className="w-3 h-3 text-[var(--tx3)] shrink-0" />
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 z-[99999] mt-1 bg-white border border-[var(--bd)] rounded-lg shadow-xl max-h-48 overflow-hidden">
          <div className="p-2 border-b border-[var(--bd)]">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Chercher…"
              className="w-full bg-[var(--surface-2)] rounded px-2 py-1 text-xs text-[var(--tx)] placeholder:text-[var(--tx3)] focus:outline-none"
            />
          </div>
          <div className="overflow-y-auto max-h-36">
            {value && (
              <button
                onClick={() => {
                  onChange('')
                  setOpen(false)
                  setQ('')
                }}
                className="w-full text-left px-3 py-1.5 text-xs text-[var(--tx3)] hover:bg-[#F0EDF8]"
              >
                — Effacer
              </button>
            )}
            {filtered.map((a) => (
              <button
                key={a.id}
                onClick={() => {
                  onChange(a.code)
                  setOpen(false)
                  setQ('')
                }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#F0EDF8] flex items-center gap-2"
              >
                <code className="text-[var(--blue-d)] font-mono w-12">{a.code}</code>
                <span className="text-[var(--tx2)] truncate">{a.name}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-xs text-[var(--tx3)]">Aucun résultat</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Modal Extourne ───────────────────────────────────────────────────────────

function ReversalModal({ entry, onClose }: { entry: JournalEntry; onClose: () => void }) {
  const qc = useQueryClient()
  const { addNotification } = useNotificationStore()
  const nav = useNavigate()
  const [reason, setReason] = useState('')

  const mutation = useMutation({
    mutationFn: () => accountingApi.reverseEntry(entry.id, { reason }),
    onSuccess: (reversal) => {
      qc.invalidateQueries({ queryKey: ['entries'] })
      addNotification({
        type: 'success',
        title: 'Extourne créée',
        message: `Réf: ${reversal.reference}`,
      })
      nav(`/accounting/entries/${reversal.id}`)
      onClose()
    },
    onError: (e: any) => {
      addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message })
    },
  })

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-[rgba(13,8,32,0.50)] backdrop-blur-sm">
      <div className="nx-card w-full max-w-2xl mx-4">
        <div className="px-5 py-4 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--tx)]">Créer une extourne</h2>
          <p className="text-xs text-[var(--tx3)] mt-1">Écriture d'origine : {entry.reference}</p>
        </div>
        <div className="p-5 space-y-4">
          <div className="px-3 py-2 bg-[var(--amber-bg)] border border-[rgba(245,158,11,0.25)] rounded-lg text-xs text-[var(--amber-text)]">
            ⚠️ L'écriture d'origine sera marquée REVERSED et ne pourra plus être modifiée.
          </div>
          <div>
            <label className="block text-xs text-[var(--tx3)] mb-1.5">Motif de l'extourne *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Décrivez précisément le motif de cette extourne (minimum 10 caractères)…"
              className="w-full bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] placeholder:text-[var(--tx3)] focus:outline-none focus:border-blue-500 resize-none"
            />
            <p className="text-xs text-[var(--tx3)] mt-1">{reason.length}/10 caractères minimum</p>
          </div>
        </div>
        <div className="flex gap-3 px-5 py-4 border-t border-[var(--bd)]">
          <button
            onClick={onClose}
            className="flex-1 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)] border border-[var(--bd)] rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={reason.length < 10 || mutation.isPending}
            className="flex-1 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-[var(--tx)] text-sm font-medium rounded-lg transition-colors"
          >
            {mutation.isPending ? 'Création…' : "Créer l'extourne"}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function JournalEntryPage() {
  const { id } = useParams<{ id?: string }>()
  const [params] = useSearchParams()
  const nav = useNavigate()
  const qc = useQueryClient()
  const { hasPermission } = useAuthStore()
  const { addNotification } = useNotificationStore()

  const isNew = !id

  // ── Data ──────────────────────────────────────────────────────────────────

  const { data: entry, isLoading: loadingEntry } = useQuery({
    queryKey: ['entry', id],
    queryFn: () => accountingApi.getEntry(id!),
    enabled: !!id,
  })

  const { data: fiscalYears = [] } = useQuery({
    queryKey: ['fiscal-years'],
    queryFn: accountingApi.getFiscalYears,
  })

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountingApi.getAccounts({ isActive: true }),
  })

  const { data: taxRules = [] } = useQuery({
    queryKey: ['tax-rules'],
    queryFn: accountingApi.getTaxRules,
  })

  const currentFY = fiscalYears.find((f) => f.status === 'OPEN' || f.status === 'SOFT_CLOSED')

  // ── State formulaire ──────────────────────────────────────────────────────

  const [form, setForm] = useState({
    fiscalYearId: params.get('fiscalYearId') ?? currentFY?.id ?? '',
    journalId: params.get('journalId') ?? '',
    date: new Date().toISOString().slice(0, 10),
    description: '',
    currency: 'XOF',
  })
  const [lines, setLines] = useState<LineForm[]>([emptyLine(), emptyLine()])
  const [showReversal, setShowReversal] = useState(false)

  // Préremplir depuis entry existante
  useEffect(() => {
    if (entry && !isNew) {
      setForm({
        fiscalYearId: entry.fiscalYearId,
        journalId: entry.journalId,
        date: entry.date.slice(0, 10),
        description: entry.description,
        currency: entry.currency,
      })
      setLines(
        entry.lines.map((l) => ({
          id: l.id,
          debitAccountCode: l.debitAccount?.code ?? '',
          creditAccountCode: l.creditAccount?.code ?? '',
          debitAmount: toNum(l.debitAmount) > 0 ? String(toNum(l.debitAmount)) : '',
          creditAmount: toNum(l.creditAmount) > 0 ? String(toNum(l.creditAmount)) : '',
          description: l.description ?? '',
          taxRuleCode: l.taxRule?.code ?? '',
        })),
      )
    }
  }, [entry, isNew])

  // ── Calculs équilibre ─────────────────────────────────────────────────────

  const totalDebit = lines.reduce((s, l) => s + (toNum(l.debitAmount) || 0), 0)
  const totalCredit = lines.reduce((s, l) => s + (toNum(l.creditAmount) || 0), 0)
  const isBalanced = Math.abs(totalDebit - totalCredit) <= 1 && totalDebit > 0

  // ── Mutations ─────────────────────────────────────────────────────────────

  const buildDto = () => ({
    fiscalYearId: form.fiscalYearId,
    journalId: form.journalId,
    date: form.date,
    description: form.description,
    currency: form.currency,
    lines: lines
      .filter((l) => l.debitAccountCode || l.creditAccountCode)
      .map((l, idx) => ({
        lineNumber: idx + 1,
        debitAccountCode: l.debitAccountCode || undefined,
        creditAccountCode: l.creditAccountCode || undefined,
        debitAmount: parseFloat(l.debitAmount) || 0,
        creditAmount: parseFloat(l.creditAmount) || 0,
        description: l.description || undefined,
        taxRuleCode: l.taxRuleCode || undefined,
      })),
  })

  const createMutation = useMutation({
    mutationFn: () => accountingApi.createEntry(buildDto()),
    onSuccess: (e) => {
      qc.invalidateQueries({ queryKey: ['entries'] })
      addNotification({ type: 'success', title: 'Écriture créée', message: e.reference })
      nav(`/accounting/entries/${e.id}`)
    },
    onError: (e: any) => {
      addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message })
    },
  })

  const validateMutation = useMutation({
    mutationFn: () => accountingApi.validateEntry(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entry', id] })
      addNotification({
        type: 'success',
        title: 'Écriture validée',
        message: "L'écriture est maintenant immuable.",
      })
    },
    onError: (e: any) => {
      addNotification({
        type: 'error',
        title: 'Erreur validation',
        message: e?.response?.data?.message,
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => accountingApi.deleteEntry(id!),
    onSuccess: () => {
      addNotification({ type: 'success', title: 'Brouillon supprimé', message: '' })
      nav(ROUTES.ACCOUNTING_ENTRIES)
    },
  })

  // ── Édition lignes ────────────────────────────────────────────────────────

  const updateLine = (i: number, patch: Partial<LineForm>) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)))

  const addLine = () => setLines((ls) => [...ls, emptyLine()])

  const removeLine = (i: number) => {
    if (lines.length <= 2) return
    setLines((ls) => ls.filter((_, idx) => idx !== i))
  }

  // ── Statut écriture ───────────────────────────────────────────────────────

  const isReadOnly = entry && !['DRAFT', 'PENDING'].includes(entry.status)
  const statusCfg = entry ? STATUS_LABELS[entry.status] : null

  if (!isNew && loadingEntry) {
    return <div className="text-center py-10 text-[var(--tx3)]">Chargement…</div>
  }

  return (
    <div className="space-y-5 nx-fade-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--tx)]">
            {isNew ? 'Nouvelle écriture' : entry?.reference}
          </h1>
          {entry && (
            <p className="text-sm text-[var(--tx3)] mt-0.5">
              {entry.fiscalYear?.label} · Journal {entry.journal?.code}
              {entry.validatedAt &&
                ` · Validée le ${new Date(entry.validatedAt).toLocaleDateString('fr-SN')}`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {statusCfg && (
            <span className={`text-xs px-3 py-1 rounded-full ${statusCfg.color}`}>
              {statusCfg.label}
            </span>
          )}
          {/* Actions selon statut */}
          {entry?.status === 'DRAFT' && hasPermission('accounting:validate_entry') && (
            <button
              onClick={() => validateMutation.mutate()}
              disabled={validateMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-[var(--tx)] text-sm font-medium rounded-lg transition-colors"
            >
              <CheckCircle className="w-4 h-4" />
              Valider
            </button>
          )}
          {entry?.status === 'DRAFT' && (
            <button
              onClick={() => deleteMutation.mutate()}
              className="flex items-center gap-2 px-3 py-2 border border-[rgba(239,68,68,0.35)] text-[var(--red-text)] hover:bg-[var(--red-bg)] text-sm rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {['VALIDATED', 'LOCKED'].includes(entry?.status ?? '') &&
            hasPermission('accounting:create_entry') && (
              <button
                onClick={() => setShowReversal(true)}
                className="flex items-center gap-2 px-4 py-2 border border-amber-500/30 text-[var(--amber-text)] hover:bg-[var(--amber-bg)] text-sm font-medium rounded-lg transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Extourner
              </button>
            )}
        </div>
      </div>

      {/* Alerte LOCKED */}
      {entry?.status === 'LOCKED' && (
        <div className="flex items-center gap-3 px-4 py-3 bg-[var(--blue-bg)] border border-[rgba(245,124,0,0.25)] rounded-xl text-[var(--blue-d)] text-sm">
          <Lock className="w-4 h-4 shrink-0" />
          Écriture verrouillée (exercice clôturé). Toute correction passe par une extourne.
        </div>
      )}

      {/* ── Entête écriture ─────────────────────────────────────────────────── */}
      <div className="nx-card p-5 shadow-sm">
        <h2 className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)] mb-4">Informations générales</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs text-[var(--tx3)] mb-1.5">Exercice *</label>
            <select
              value={form.fiscalYearId}
              onChange={(e) => setForm((f) => ({ ...f, fiscalYearId: e.target.value }))}
              disabled={isReadOnly}
              className="w-full bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500 disabled:opacity-60"
            >
              <option value="">Sélectionner…</option>
              {fiscalYears.map((fy) => (
                <option key={fy.id} value={fy.id}>
                  {fy.label} ({fy.status})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-[var(--tx3)] mb-1.5">Date *</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              disabled={isReadOnly}
              className="w-full bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500 disabled:opacity-60"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-[var(--tx3)] mb-1.5">Libellé *</label>
            <input
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              disabled={isReadOnly}
              placeholder="Description de l'écriture…"
              className="w-full bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] placeholder:text-[var(--tx3)] focus:outline-none focus:border-blue-500 disabled:opacity-60"
            />
          </div>
        </div>
      </div>

      {/* ── Lignes ─────────────────────────────────────────────────────────── */}
      <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--bd)] flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[var(--tx)]">Lignes d'écriture</h2>
          <div className="flex items-center gap-2">
            {/* Indicateur équilibre */}
            {totalDebit > 0 && (
              <div
                className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${isBalanced ? 'bg-[var(--green)]/10 text-[var(--green-text)]' : 'bg-[var(--red-bg)] text-[var(--red-text)]'}`}
              >
                {isBalanced ? (
                  <CheckCircle className="w-3 h-3" />
                ) : (
                  <AlertTriangle className="w-3 h-3" />
                )}
                {isBalanced
                  ? 'Équilibrée'
                  : `Écart ${formatXOF(Math.abs(totalDebit - totalCredit))}`}
              </div>
            )}
          </div>
        </div>

        {/* Tableau */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--bd)] text-[var(--tx3)]">
                <th className="text-left px-3 py-2 w-6">#</th>
                <th className="text-left px-3 py-2 min-w-[180px]">Compte débit</th>
                <th className="text-left px-3 py-2 min-w-[180px]">Compte crédit</th>
                <th className="text-right px-3 py-2 w-28">Débit (XOF)</th>
                <th className="text-right px-3 py-2 w-28">Crédit (XOF)</th>
                <th className="text-left px-3 py-2 min-w-[120px]">Libellé</th>
                <th className="text-left px-3 py-2 w-28">Taxe</th>
                {!isReadOnly && <th className="w-8" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {lines.map((line, i) => (
                <tr key={line.id} className="hover:bg-[var(--surface-2)]/20 group">
                  <td className="px-3 py-2 text-[var(--tx3)]">{i + 1}</td>
                  <td className="px-3 py-2">
                    {isReadOnly ? (
                      <span className="text-[var(--blue-d)] font-mono">
                        {entry?.lines[i]?.debitAccount?.code}
                        {entry?.lines[i]?.debitAccount && (
                          <span className="text-[var(--tx3)] font-sans">
                            {' '}
                            — {entry.lines[i].debitAccount!.name}
                          </span>
                        )}
                      </span>
                    ) : (
                      <AccountSelect
                        value={line.debitAccountCode}
                        onChange={(v) => updateLine(i, { debitAccountCode: v })}
                        accounts={accounts}
                        placeholder="Compte débit…"
                      />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isReadOnly ? (
                      <span className="text-[var(--blue-d)] font-mono">
                        {entry?.lines[i]?.creditAccount?.code}
                        {entry?.lines[i]?.creditAccount && (
                          <span className="text-[var(--tx3)] font-sans">
                            {' '}
                            — {entry.lines[i].creditAccount!.name}
                          </span>
                        )}
                      </span>
                    ) : (
                      <AccountSelect
                        value={line.creditAccountCode}
                        onChange={(v) => updateLine(i, { creditAccountCode: v })}
                        accounts={accounts}
                        placeholder="Compte crédit…"
                      />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isReadOnly ? (
                      <span className="text-right block text-[var(--tx)]">
                        {line.debitAmount ? formatXOF(parseFloat(line.debitAmount)) : '—'}
                      </span>
                    ) : (
                      <input
                        type="number"
                        value={line.debitAmount}
                        onChange={(e) => updateLine(i, { debitAmount: e.target.value })}
                        placeholder="0"
                        className="w-full bg-transparent border border-[var(--bd)] rounded px-2 py-1 text-right text-[var(--tx)] placeholder:text-[var(--tx4)] focus:outline-none focus:border-blue-500"
                      />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isReadOnly ? (
                      <span className="text-right block text-[var(--tx)]">
                        {line.creditAmount ? formatXOF(parseFloat(line.creditAmount)) : '—'}
                      </span>
                    ) : (
                      <input
                        type="number"
                        value={line.creditAmount}
                        onChange={(e) => updateLine(i, { creditAmount: e.target.value })}
                        placeholder="0"
                        className="w-full bg-transparent border border-[var(--bd)] rounded px-2 py-1 text-right text-[var(--tx)] placeholder:text-[var(--tx4)] focus:outline-none focus:border-blue-500"
                      />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isReadOnly ? (
                      <span className="text-[var(--tx2)]">{entry?.lines[i]?.description}</span>
                    ) : (
                      <input
                        value={line.description}
                        onChange={(e) => updateLine(i, { description: e.target.value })}
                        placeholder="Libellé ligne"
                        className="w-full bg-transparent border border-[var(--bd)] rounded px-2 py-1 text-xs text-[var(--tx)] placeholder:text-[var(--tx4)] focus:outline-none focus:border-blue-500"
                      />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isReadOnly ? (
                      <span className="text-[var(--tx3)]">{entry?.lines[i]?.taxRule?.code}</span>
                    ) : (
                      <select
                        value={line.taxRuleCode}
                        onChange={(e) => updateLine(i, { taxRuleCode: e.target.value })}
                        className="w-full bg-[var(--surface-2)] border border-[var(--bd)] rounded px-1 py-1 text-xs text-[var(--tx)] focus:outline-none focus:border-blue-500"
                      >
                        <option value="">—</option>
                        {taxRules.map((t) => (
                          <option key={t.id} value={t.code}>
                            {t.code} ({parseFloat(String(t.rate))}%)
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  {!isReadOnly && (
                    <td className="px-2 py-2">
                      <button
                        onClick={() => removeLine(i)}
                        disabled={lines.length <= 2}
                        className="opacity-0 group-hover:opacity-100 p-1 text-[var(--tx3)] hover:text-[var(--red-text)] disabled:opacity-0 transition-all"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {/* Totaux */}
            <tfoot>
              <tr className="border-t border-[var(--bd)] bg-[var(--surface-2)]/20">
                <td colSpan={3} className="px-3 py-2 text-xs text-[var(--tx3)] text-right font-medium">
                  TOTAUX
                </td>
                <td
                  className={`px-3 py-2 text-right text-xs font-bold ${isBalanced ? 'text-[var(--green-text)]' : 'text-[var(--red-text)]'}`}
                >
                  {formatXOF(totalDebit)}
                </td>
                <td
                  className={`px-3 py-2 text-right text-xs font-bold ${isBalanced ? 'text-[var(--green-text)]' : 'text-[var(--red-text)]'}`}
                >
                  {formatXOF(totalCredit)}
                </td>
                <td colSpan={isReadOnly ? 2 : 3} />
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Boutons bas tableau */}
        {!isReadOnly && (
          <div className="px-5 py-3 border-t border-[var(--bd)] flex items-center justify-between">
            <button
              onClick={addLine}
              className="flex items-center gap-2 text-xs text-[var(--blue-d)] hover:text-blue-300 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Ajouter une ligne
            </button>

            <div className="flex items-center gap-3">
              <button
                onClick={() => nav(ROUTES.ACCOUNTING_ENTRIES)}
                className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)] transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => createMutation.mutate()}
                disabled={!isBalanced || !form.description || createMutation.isPending}
                className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {createMutation.isPending
                  ? 'Enregistrement…'
                  : isNew
                    ? 'Enregistrer brouillon'
                    : 'Mettre à jour'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pièces justificatives */}
      {entry && (
        <div className="nx-card p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[var(--tx)]">Pièces justificatives</h3>
            {!isReadOnly && (
              <button className="flex items-center gap-1.5 text-xs text-[var(--blue-d)] hover:text-blue-300">
                <Upload className="w-3.5 h-3.5" />
                Joindre
              </button>
            )}
          </div>
          {entry.attachments?.length === 0 ? (
            <p className="text-xs text-[var(--tx3)]">Aucune pièce jointe</p>
          ) : (
            <div className="space-y-1">
              {entry.attachments?.map((att) => (
                <div
                  key={att.id}
                  className="flex items-center gap-3 px-3 py-2 bg-[var(--surface-2)]/30 rounded-lg text-xs"
                >
                  <span className="text-[var(--tx2)] flex-1">{att.fileName}</span>
                  {att.isWormSealed ? (
                    <span className="text-[var(--green-text)] flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Scellé WORM
                    </span>
                  ) : (
                    <span className="text-[var(--amber-text)]">Non scellé</span>
                  )}
                  <button className="text-[var(--tx3)] hover:text-[var(--tx)]">
                    <Eye className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showReversal && entry && (
        <ReversalModal entry={entry} onClose={() => setShowReversal(false)} />
      )}
    </div>
  )
}
